import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import ArticleCard from "@/components/base/ArticleCard";
import FaqAccordion from "@/components/feature/FaqAccordion";
import HeroSection from "@/pages/home/components/HeroSection";
import FeaturedSection from "@/pages/home/components/FeaturedSection";
import StatBoard from "@/pages/home/components/StatBoard";
import TopicSection from "@/pages/home/components/TopicSection";
import VoiceSection from "@/pages/home/components/VoiceSection";
import SubscribeSection from "@/pages/home/components/SubscribeSection";
import { articles, type Article } from "@/mocks/articles";
import { faqs } from "@/mocks/faq";
import { useSeo, SITE_NAME, SITE_ORIGIN } from "@/hooks/useSeo";

const sorted = [...articles].sort((a, b) => b.date.localeCompare(a.date));
const latest = sorted.slice(0, 6);
const main = articles.find((item) => item.id === "midnight-vote-spike") ?? articles[0];
const side = ["cost-of-one-vote", "five-inflation-tricks", "fan-club-48-hours"]
  .map((id) => articles.find((item) => item.id === id))
  .filter((item): item is Article => Boolean(item));

export default function Home() {
  useSeo({
    title: "选美吃瓜｜投票打榜疑云与票数注水调查 - 瓜圈显微镜",
    description:
      "瓜圈显微镜聚焦选美吃瓜投票打榜环节，追踪票数注水疑云、粉丝内战拉票操作与排名数据异常，用可读的调查与数据观察，带你看清人气榜单背后的真实生态，判断投票排名到底靠不靠谱。",
    keywords: "选美吃瓜,选美吃瓜投票,选美吃瓜打榜,选美吃瓜票数,选美吃瓜数据注水",
    path: "/",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: SITE_NAME,
        url: `${SITE_ORIGIN}/`,
        inLanguage: "zh-CN",
        potentialAction: {
          "@type": "SearchAction",
          target: `${SITE_ORIGIN}/ranking?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: SITE_NAME,
        url: `${SITE_ORIGIN}/`,
        email: "editor@guaguan-microscope.example.com",
        description: "专注选美赛事投票打榜环节的内容观察站。",
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: { "@type": "Answer", text: item.answer },
        })),
      },
    ],
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader variant="overlay" />
      <main className="flex-1">
        <HeroSection />

        <FeaturedSection main={main} side={side} />

        <StatBoard />

        <section className="px-4 md:px-6 xl:px-10 py-14 md:py-20">
          <div className="max-w-[1320px] mx-auto">
            <div className="flex items-end justify-between gap-4 mb-8">
              <div>
                <p className="text-xs tracking-[0.3em] text-primary-600 mb-2">LATEST</p>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">最新瓜文</h2>
              </div>
              <Link
                to="/ranking"
                className="hidden sm:inline-flex items-center gap-1 text-sm text-primary-700 hover:text-primary-800 whitespace-nowrap cursor-pointer"
              >
                更多内容
                <i className="ri-arrow-right-line" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {latest.map((item) => (
                <ArticleCard key={item.id} article={item} />
              ))}
            </div>
          </div>
        </section>

        <TopicSection />

        <VoiceSection />

        <section className="bg-background-100 border-t border-background-200">
          <div className="px-4 md:px-6 xl:px-10 py-14 md:py-20">
            <div className="max-w-[900px] mx-auto">
              <div className="text-center mb-10">
                <p className="text-xs tracking-[0.3em] text-primary-600 mb-2">FAQ</p>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  关于投票打榜，你可能想问
                </h2>
                <p className="mt-3 text-sm text-foreground-600">
                  下面这些问题是读者问得最多的，也是理解榜单可信度的起点。
                </p>
              </div>
              <FaqAccordion items={faqs} />
            </div>
          </div>
        </section>

        <SubscribeSection />
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}